import React, { useState } from 'react';

// Asylum Step 4: Evidence Collection
export const AsylumStep4EvidenceCollection = ({ formData, setFormData, onNext, onBack }) => {
  const [documents, setDocuments] = useState(formData.documents || []);
  const [uploadCategory, setUploadCategory] = useState('medical');

  const documentCategories = [
    {
      id: 'medical',
      title: 'Medical Records',
      description: 'Medical reports, psychological evaluations, treatment records',
      icon: '🏥',
      examples: ['Hospital records', 'Psychological evaluation', 'Medical treatment', 'Therapy records']
    },
    {
      id: 'country-conditions',
      title: 'Country Condition Evidence',
      description: 'Reports about conditions in your home country',
      icon: '📰',
      examples: ['State Department reports', 'Human rights reports', 'News articles', 'NGO reports']
    },
    {
      id: 'personal-documents',
      title: 'Personal Documents',
      description: 'Identity documents, certificates, personal records',
      icon: '📄',
      examples: ['Birth certificate', 'Marriage certificate', 'Educational certificates', 'Employment records']
    },
    {
      id: 'witness-statements',
      title: 'Witness Statements',
      description: 'Affidavits and statements from people who know your story',
      icon: '👥',
      examples: ['Family affidavits', 'Friend statements', 'Expert witness statements', 'Community leader letters']
    },
    {
      id: 'photographs',
      title: 'Photographs',
      description: 'Photos documenting persecution, injuries, or conditions',
      icon: '📸',
      examples: ['Injury photos', 'Protest photos', 'Threats documentation', 'Living conditions']
    },
    {
      id: 'correspondence',
      title: 'Correspondence',
      description: 'Letters, emails, messages related to persecution',
      icon: '✉️',
      examples: ['Threat letters', 'Email correspondence', 'Social media messages', 'Official notices']
    }
  ];

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    const newDocuments = [];
    
    for (const file of files) {
      try {
        // Convert file to base64 for OCR analysis
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        console.log(`🔍 Analyzing document: ${file.name} (${file.type}, ${file.size} bytes)`);

        // Call backend for actual document analysis with Google Vision OCR
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            document_file: fileData,
            document_name: file.name,
            document_type: uploadCategory,
            case_context: {
              case_type: 'Asylum & Protection Application',
              agent_type: 'asylum-protection',
              applicant: formData.personalInfo?.fullName || 'Asylum Applicant',
              protection_type: formData.protectionType || 'asylum'
            }
          })
        });

        let documentAnalysis;
        if (response.ok) {
          documentAnalysis = await response.json();
          console.log(`✅ Document analysis successful for ${file.name}:`, documentAnalysis);
        } else {
          const errorText = await response.text();
          console.error(`❌ Document analysis failed for ${file.name}:`, response.status, errorText);
          documentAnalysis = {
            document_type: uploadCategory,
            content_description: `Document analysis failed: ${errorText}`,
            case_relevance: "Manual review required to determine relevance",
            exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
            immigration_value: "Medium",
            analysis_error: `Analysis failed: ${errorText}`
          };
        }

        // Create document object with OCR analysis results
        const newDoc = {
          id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
          name: file.name,
          category: uploadCategory,
          file: file,
          timestamp: new Date().toISOString(),
          size: file.size,
          // Add OCR analysis results
          analysis: documentAnalysis,
          content_description: documentAnalysis.content_description,
          parties_mentioned: documentAnalysis.parties_mentioned,
          key_dates: documentAnalysis.key_dates,
          case_relevance: documentAnalysis.case_relevance,
          exhibit_description: documentAnalysis.exhibit_description,
          immigration_value: documentAnalysis.immigration_value,
          red_flags: documentAnalysis.red_flags,
          authenticity_indicators: documentAnalysis.authenticity_indicators,
          extracted_text: documentAnalysis.extracted_text
        };

        newDocuments.push(newDoc);
        
      } catch (error) {
        console.error('Document analysis error:', error);
        
        // Add document with error indication
        const newDoc = {
          id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
          name: file.name,
          category: uploadCategory,
          file: file,
          timestamp: new Date().toISOString(),
          size: file.size,
          analysis_error: "Analysis failed - manual review required",
          content_description: `Document analysis failed for ${file.name}`,
          case_relevance: "Manual review required",
          exhibit_description: `Exhibit: ${file.name} (manual review required)`,
          immigration_value: "Unknown"
        };
        
        newDocuments.push(newDoc);
      }
    }

    const updatedDocuments = [...documents, ...newDocuments];
    setDocuments(updatedDocuments);
    setFormData({ ...formData, documents: updatedDocuments });
  };

  const removeDocument = (docId) => {
    const updatedDocuments = documents.filter(doc => doc.id !== docId);
    setDocuments(updatedDocuments);
    setFormData({ ...formData, documents: updatedDocuments });
  };

  const isValidForm = () => {
    return documents.length >= 1; // At least one document
  };

  const handleContinue = () => {
    if (isValidForm()) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Evidence Collection</h3>
        <p className="text-gray-400 text-lg">Upload supporting documents for your protection case</p>
      </div>

      {/* Document Category Selection */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📂 Select Document Category</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documentCategories.map((category) => (
            <div
              key={category.id}
              onClick={() => setUploadCategory(category.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                uploadCategory === category.id
                  ? 'border-red-500 bg-red-900/20'
                  : 'border-gray-600 bg-gray-700 hover:border-red-400'
              }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-2">{category.icon}</div>
                <h5 className="text-white font-medium mb-1">{category.title}</h5>
                <p className="text-gray-400 text-xs">{category.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* File Upload */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📤 Upload Documents</h4>
        
        {/* Current Category Info */}
        <div className="mb-4 p-4 bg-gray-700 rounded">
          <div className="flex items-center mb-2">
            <span className="text-2xl mr-3">
              {documentCategories.find(c => c.id === uploadCategory)?.icon}
            </span>
            <h5 className="text-white font-medium">
              {documentCategories.find(c => c.id === uploadCategory)?.title}
            </h5>
          </div>
          <p className="text-gray-300 text-sm mb-2">Examples of documents in this category:</p>
          <ul className="text-gray-400 text-sm space-y-1">
            {documentCategories.find(c => c.id === uploadCategory)?.examples.map((example, index) => (
              <li key={index}>• {example}</li>
            ))}
          </ul>
        </div>

        {/* File Input */}
        <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <div className="text-6xl text-gray-500 mb-4">📁</div>
            <h4 className="text-xl text-white mb-2">Drop files here or click to upload</h4>
            <p className="text-gray-400 text-sm">
              Supported formats: PDF, Word, Images, Text files
            </p>
          </label>
        </div>
      </div>

      {/* Uploaded Documents */}
      {documents.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">📋 Uploaded Documents ({documents.length})</h4>
          <div className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-700 rounded">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">
                    {documentCategories.find(c => c.id === doc.category)?.icon || '📄'}
                  </span>
                  <div>
                    <h5 className="text-white font-medium">{doc.name}</h5>
                    <p className="text-gray-400 text-sm">
                      {documentCategories.find(c => c.id === doc.category)?.title} • 
                      {(doc.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeDocument(doc.id)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document Guidelines */}
      <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-2">📋 Document Guidelines</h4>
        <ul className="text-blue-200 text-sm space-y-1">
          <li>• Upload clear, legible copies of documents</li>
          <li>• Translate non-English documents when possible</li>
          <li>• Medical records strengthen persecution claims</li>
          <li>• Country condition evidence supports your case</li>
          <li>• Personal witness statements are valuable evidence</li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Persecution Documentation
        </button>
        <button
          onClick={handleContinue}
          disabled={!isValidForm()}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            isValidForm()
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Country Analysis →
        </button>
      </div>
    </div>
  );
};

// Asylum Step 5: Country Conditions Analysis
export const AsylumStep5CountryAnalysis = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(formData.analysisResults || {});

  const performAnalysis = async () => {
    setAnalyzing(true);
    
    try {
      // Real API call to analyze asylum case
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ai/analyze-case`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          caseType: 'asylum-protection',
          petitioner_details: {
            fullName: "Protection Seeker",
            status: formData.caseType || "asylum-seeker",
            country: formData.countryOfOrigin || "Unknown",
            location: formData.personalDetails?.currentLocation || "US",
            caseType: formData.caseType
          },
          beneficiary_details: {
            fullName: "Protection Seeker", 
            country: formData.countryOfOrigin || "Unknown",
            location: formData.personalDetails?.currentLocation || "US",
            age: formData.personalDetails?.age || "Unknown"
          },
          relationship_details: {
            persecutionType: formData.persecutionDetails?.typeOfPersecution?.join(', ') || "",
            persecutors: formData.persecutionDetails?.persecutors || "",
            governmentInvolvement: formData.persecutionDetails?.governmentInvolvement || "",
            reasonForLeaving: formData.countryDetails?.reasonForLeaving || "",
            arrivalDate: formData.arrivalDate || "",
            safetyLevel: formData.safetyStatus?.currentSafety || ""
          },
          documents: formData.documents || []
        })
      });

      if (response.ok) {
        const result = await response.json();
        setAnalysisResults(result);
        setFormData({ ...formData, analysisResults: result });
      } else {
        // Fallback to dynamic analysis if API fails
        const dynamicAnalysis = generateAsylumAnalysis(formData);
        setAnalysisResults(dynamicAnalysis);
        setFormData({ ...formData, analysisResults: dynamicAnalysis });
      }
    } catch (error) {
      console.error('Analysis error:', error);
      // Fallback to dynamic analysis
      const dynamicAnalysis = generateAsylumAnalysis(formData);
      setAnalysisResults(dynamicAnalysis);
      setFormData({ ...formData, analysisResults: dynamicAnalysis });
    }
    
    setAnalyzing(false);
  };

  const generateAsylumAnalysis = (data) => {
    const documents = data.documents || [];
    const persecutionTypes = data.persecutionDetails?.typeOfPersecution || [];
    const safetyLevel = data.safetyStatus?.currentSafety || '';
    const caseType = data.caseType || '';
    const governmentInvolvement = data.persecutionDetails?.governmentInvolvement || '';
    const persecutors = data.persecutionDetails?.persecutors || '';
    
    // Detailed scoring calculation with metrics breakdown
    let baseScore = 20; // Minimum base score
    let persecutionScore = 0;
    let evidenceScore = 0;
    let credibilityScore = 0;
    let governmentScore = 0;
    let timelineScore = 0;

    // Persecution Analysis (0-30 points)
    if (persecutionTypes.length > 0) {
      persecutionScore += 15; // Basic persecution claim
      
      // Additional points for strong protected grounds
      if (persecutionTypes.includes('political') || persecutionTypes.includes('religious')) {
        persecutionScore += 8; // Strong traditional grounds
      }
      if (persecutionTypes.includes('nationality') || persecutionTypes.includes('social-group')) {
        persecutionScore += 5; // Well-established grounds
      }
      if (persecutionTypes.includes('gender') || persecutionTypes.includes('sexual-orientation')) {
        persecutionScore += 6; // Evolving but recognized grounds
      }
      
      // Multiple grounds strengthen case
      if (persecutionTypes.length > 1) {
        persecutionScore += 3;
      }
    }
    persecutionScore = Math.min(30, persecutionScore);

    // Evidence Quality Analysis (0-25 points)
    const medicalDocs = documents.filter(d => d.category === 'medical').length;
    const countryDocs = documents.filter(d => d.category === 'country-conditions').length;
    const personalDocs = documents.filter(d => d.category === 'personal-documents').length;
    const witnessStmts = documents.filter(d => d.category === 'witness-statements').length;
    const photographs = documents.filter(d => d.category === 'photographs').length;
    const correspondence = documents.filter(d => d.category === 'correspondence').length;
    
    if (medicalDocs > 0) evidenceScore += 8; // Medical evidence crucial
    if (countryDocs > 0) evidenceScore += 6; // Country conditions important
    if (personalDocs > 0) evidenceScore += 4; // Identity documents
    if (witnessStmts > 0) evidenceScore += 4; // Corroborating testimony
    if (photographs > 0) evidenceScore += 2; // Visual evidence
    if (correspondence > 0) evidenceScore += 3; // Documentary evidence
    
    // Bonus for comprehensive documentation
    if (documents.length >= 8) evidenceScore += 3;
    evidenceScore = Math.min(25, evidenceScore);

    // Government Involvement (0-20 points)
    switch(governmentInvolvement) {
      case 'government-perpetrator':
        governmentScore = 20; // Strongest case
        break;
      case 'government-unable':
        governmentScore = 15; // Strong case
        break;
      case 'government-unwilling':
        governmentScore = 17; // Very strong case
        break;
      case 'government-complicit':
        governmentScore = 18; // Very strong case
        break;
      default:
        governmentScore = 5; // Weak on this element
    }

    // Credibility Factors (0-15 points)
    if (data.persecutionDetails?.detailedAccount) credibilityScore += 8;
    if (data.persecutionDetails?.physicalHarm) credibilityScore += 3;
    if (data.persecutionDetails?.psychologicalHarm) credibilityScore += 2;
    if (data.persecutionDetails?.witnessesExist) credibilityScore += 2;
    credibilityScore = Math.min(15, credibilityScore);

    // Timeline Considerations (0-10 points) - Asylum specific
    if (caseType === 'asylum' && data.arrivalDate) {
      const arrival = new Date(data.arrivalDate);
      const now = new Date();
      const daysSinceArrival = (now - arrival) / (1000 * 60 * 60 * 24);
      
      if (daysSinceArrival <= 365) {
        timelineScore = 10; // Within one year
      } else if (daysSinceArrival <= 400) {
        timelineScore = 7; // Close to deadline
      } else {
        timelineScore = 2; // Past deadline - need exceptions
      }
    } else {
      timelineScore = 8; // No timeline issues for non-asylum cases
    }

    // Case-specific adjustments
    if (caseType === 'vawa') {
      // VAWA-specific scoring adjustments
      const hasAbuseEvidence = medicalDocs > 0 || photographs > 0 || correspondence > 0;
      if (hasAbuseEvidence) evidenceScore += 5;
      
      // Relationship evidence important for VAWA
      if (personalDocs > 0) evidenceScore += 3;
      
      timelineScore = 10; // No filing deadline for VAWA
    }

    if (caseType === 't-visa' || caseType === 'u-visa') {
      // Law enforcement cooperation crucial
      const hasLawEnforcementDocs = documents.some(d => 
        d.name.toLowerCase().includes('police') || 
        d.name.toLowerCase().includes('certification') ||
        d.category === 'correspondence'
      );
      if (hasLawEnforcementDocs) evidenceScore += 8;
    }

    const finalScore = Math.min(100, Math.max(0, baseScore + persecutionScore + evidenceScore + governmentScore + credibilityScore + timelineScore));
    
    // Generate detailed strengths and issues with metrics
    const strengths = [];
    const issues = [];
    
    if (persecutionScore >= 20) strengths.push(`Strong persecution claims (${persecutionScore}/30 points)`);
    if (evidenceScore >= 18) strengths.push(`Comprehensive evidence package (${evidenceScore}/25 points)`);
    if (governmentScore >= 15) strengths.push(`Government unable/unwilling to protect established (${governmentScore}/20 points)`);
    if (credibilityScore >= 12) strengths.push(`Strong credibility indicators (${credibilityScore}/15 points)`);
    if (timelineScore >= 8) strengths.push(`No significant timeline issues (${timelineScore}/10 points)`);
    
    if (persecutionScore < 15) issues.push(`Persecution grounds need strengthening (${persecutionScore}/30 points)`);
    if (evidenceScore < 12) issues.push(`Evidence package needs enhancement (${evidenceScore}/25 points)`);
    if (governmentScore < 10) issues.push(`Government protection analysis weak (${governmentScore}/20 points)`);
    if (credibilityScore < 8) issues.push(`Credibility factors need development (${credibilityScore}/15 points)`);
    if (timelineScore < 6) issues.push(`Timeline concerns require attention (${timelineScore}/10 points)`);

    // Case-specific analysis
    let caseSpecificAnalysis = {};
    if (caseType === 'vawa') {
      const vawaAbuseEvidence = medicalDocs > 0 || photographs > 0 || correspondence > 0;
      caseSpecificAnalysis = {
        abuse_evidence: vawaAbuseEvidence ? 'Documented' : 'Needs documentation',
        relationship_evidence: personalDocs > 0 ? 'Available' : 'Missing',
        good_moral_character: 'Needs assessment',
        extreme_hardship: 'Needs documentation'
      };
    }

    return {
      overall_score: finalScore,
      case_strength: finalScore >= 75 ? 'Strong' : finalScore >= 60 ? 'Moderate' : finalScore >= 40 ? 'Developing' : 'Needs Strengthening',
      score_breakdown: {
        persecution_analysis: persecutionScore,
        evidence_quality: evidenceScore,
        government_protection: governmentScore,
        credibility_factors: credibilityScore,
        timeline_compliance: timelineScore,
        base_score: baseScore
      },
      persecution_analysis: {
        grounds: persecutionTypes.join(', ') || 'Not specified',
        government_involvement: governmentInvolvement || 'Not specified',
        severity: finalScore >= 70 ? 'High' : finalScore >= 50 ? 'Moderate' : 'Low',
        strength_score: `${persecutionScore}/30`
      },
      document_analysis: {
        total_documents: documents.length,
        medical_evidence: medicalDocs,
        country_evidence: countryDocs,
        personal_documents: personalDocs,
        witness_statements: witnessStmts,
        photographs: photographs,
        correspondence: correspondence,
        completeness: documents.length >= 8 ? 'Comprehensive' : documents.length >= 5 ? 'Adequate' : 'Limited',
        evidence_score: `${evidenceScore}/25`
      },
      case_specific_analysis: caseSpecificAnalysis,
      strengths: strengths.length > 0 ? strengths : ['Case information collected'],
      issues: issues.length > 0 ? issues : ['No major issues identified'],
      recommendations: generateAsylumRecommendations(data, {
        persecution: persecutionScore,
        evidence: evidenceScore,
        government: governmentScore,
        credibility: credibilityScore,
        timeline: timelineScore
      })
    };
  };

  const generateAsylumRecommendations = (data) => {
    const recommendations = [];
    const documents = data.documents || [];
    const medicalDocs = documents.filter(d => d.category === 'medical').length;
    const countryDocs = documents.filter(d => d.category === 'country-conditions').length;
    
    if (medicalDocs === 0) {
      recommendations.push('Obtain medical/psychological evaluation to document persecution effects');
    }
    if (countryDocs === 0) {
      recommendations.push('Gather country condition reports from State Department and human rights organizations');
    }
    if (documents.length < 5) {
      recommendations.push('Collect additional supporting documentation to strengthen case');
    }
    if (!data.persecutionDetails?.witnessesExist) {
      recommendations.push('Consider obtaining witness affidavits from people who know your story');
    }
    
    return recommendations.length > 0 ? recommendations : ['Case appears well-documented'];
  };

  React.useEffect(() => {
    if (Object.keys(analysisResults).length === 0) {
      performAnalysis();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Country Conditions Analysis</h3>
        <p className="text-gray-400 text-lg">AI-powered analysis of your protection case strength</p>
      </div>

      {analyzing && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Analyzing Your Protection Case</h4>
          <p className="text-gray-400">Evaluating persecution claims, evidence, and country conditions...</p>
        </div>
      )}

      {!analyzing && Object.keys(analysisResults).length > 0 && (
        <>
          {/* Overall Assessment */}
          <div className="bg-gradient-to-r from-red-900/20 to-gray-900/20 p-8 rounded-lg border border-red-400/30">
            <div className="text-center">
              <div className="text-6xl font-bold text-red-400 mb-2">{analysisResults.overall_score}%</div>
              <div className="text-xl text-white mb-4">Case Strength Assessment</div>
              <div className={`text-lg ${
                analysisResults.overall_score >= 70 ? 'text-emerald-400' :
                analysisResults.overall_score >= 50 ? 'text-yellow-400' :
                'text-red-400'
              }`}>
                {analysisResults.case_strength}
              </div>
            </div>
          </div>

          {/* Detailed Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">🎯 Score Breakdown</h4>
              <div className="space-y-3">
                {analysisResults.score_breakdown && Object.entries(analysisResults.score_breakdown).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-300 capitalize">{key.replace('_', ' ')}:</span>
                    <span className="text-red-400 font-medium">{value} pts</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">📋 Evidence Breakdown</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Total Documents:</span>
                  <span className="text-red-400 font-medium">{analysisResults.document_analysis?.total_documents}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Medical Evidence:</span>
                  <span className="text-red-400 font-medium">{analysisResults.document_analysis?.medical_evidence}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Country Evidence:</span>
                  <span className="text-red-400 font-medium">{analysisResults.document_analysis?.country_evidence}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Witness Statements:</span>
                  <span className="text-red-400 font-medium">{analysisResults.document_analysis?.witness_statements}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Evidence Score:</span>
                  <span className={`font-medium ${
                    analysisResults.document_analysis?.evidence_score?.split('/')[0] >= 18 ? 'text-emerald-400' :
                    analysisResults.document_analysis?.evidence_score?.split('/')[0] >= 12 ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.document_analysis?.evidence_score}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Case-Specific Analysis */}
          {analysisResults.case_specific_analysis && Object.keys(analysisResults.case_specific_analysis).length > 0 && (
            <div className="bg-purple-900/20 border border-purple-400 p-6 rounded-lg">
              <h4 className="text-purple-300 font-semibold mb-4">🔍 Case-Specific Analysis</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(analysisResults.case_specific_analysis).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-purple-200 capitalize">{key.replace('_', ' ')}:</span>
                    <span className={`font-medium ${
                      value === 'Documented' || value === 'Available' ? 'text-emerald-400' :
                      value === 'Needs documentation' || value === 'Missing' ? 'text-red-400' :
                      'text-yellow-400'
                    }`}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Traditional Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">🎯 Persecution Analysis</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Grounds:</span>
                  <span className="text-red-400 font-medium">{analysisResults.persecution_analysis?.grounds}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Government Role:</span>
                  <span className="text-red-400 font-medium">{analysisResults.persecution_analysis?.government_involvement}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Severity:</span>
                  <span className={`font-medium ${
                    analysisResults.persecution_analysis?.severity === 'High' ? 'text-red-400' :
                    analysisResults.persecution_analysis?.severity === 'Moderate' ? 'text-yellow-400' :
                    'text-gray-400'
                  }`}>
                    {analysisResults.persecution_analysis?.severity}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Strength Score:</span>
                  <span className="text-red-400 font-medium">{analysisResults.persecution_analysis?.strength_score}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">📋 Document Summary</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Personal Documents:</span>
                  <span className="text-red-400 font-medium">{analysisResults.document_analysis?.personal_documents}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Photographs:</span>
                  <span className="text-red-400 font-medium">{analysisResults.document_analysis?.photographs}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Correspondence:</span>
                  <span className="text-red-400 font-medium">{analysisResults.document_analysis?.correspondence}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Completeness:</span>
                  <span className={`font-medium ${
                    analysisResults.document_analysis?.completeness === 'Comprehensive' ? 'text-emerald-400' :
                    analysisResults.document_analysis?.completeness === 'Adequate' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.document_analysis?.completeness}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Strengths and Issues */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
              <h4 className="text-emerald-300 font-semibold mb-4">✅ Case Strengths</h4>
              <ul className="space-y-2">
                {analysisResults.strengths?.map((strength, index) => (
                  <li key={index} className="text-emerald-200 text-sm flex items-start">
                    <span className="text-emerald-400 mr-2 mt-1">•</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-yellow-900/20 border border-yellow-400 p-6 rounded-lg">
              <h4 className="text-yellow-300 font-semibold mb-4">⚠️ Areas for Improvement</h4>
              <ul className="space-y-2">
                {analysisResults.issues?.map((issue, index) => (
                  <li key={index} className="text-yellow-200 text-sm flex items-start">
                    <span className="text-yellow-400 mr-2 mt-1">•</span>
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
            <h4 className="text-blue-300 font-semibold mb-4">💡 Recommendations</h4>
            <ul className="space-y-2">
              {analysisResults.recommendations?.map((rec, index) => (
                <li key={index} className="text-blue-200 text-sm flex items-start">
                  <span className="text-blue-400 mr-2 mt-1">•</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Evidence Collection
        </button>
        <button
          onClick={onNext}
          disabled={analyzing}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !analyzing
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Legal Strategy →
        </button>
      </div>
    </div>
  );
};